import Header from './Header';
import Footer from './Footer';
import AccessibilityWidget from './AccessibilityWidget';
import { Info, AlertTriangle, ThermometerSun, Home, Sun } from 'lucide-react';

const SIZING_DATA = [
  { tons: '1.5 Ton', sqft: '900 - 1,200 sq ft' },
  { tons: '2.0 Ton', sqft: '1,200 - 1,500 sq ft' },
  { tons: '2.5 Ton', sqft: '1,500 - 1,800 sq ft' },
  { tons: '3.0 Ton', sqft: '1,800 - 2,200 sq ft' },
  { tons: '3.5 Ton', sqft: '2,200 - 2,600 sq ft' },
  { tons: '4.0 Ton', sqft: '2,600 - 3,000 sq ft' },
  { tons: '5.0 Ton', sqft: '3,000+ sq ft' },
];

const MODEL_DATA = [
  { digit: '18', tons: '1.5 Tons' },
  { digit: '24', tons: '2.0 Tons' },
  { digit: '30', tons: '2.5 Tons' },
  { digit: '36', tons: '3.0 Tons' },
  { digit: '42', tons: '3.5 Tons' },
  { digit: '48', tons: '4.0 Tons' },
  { digit: '60', tons: '5.0 Tons' },
];

export default function SizingChartPage({ onOpenLeadForm, onOpenEstimateForm }) {
  return (
    <div className="min-h-screen bg-white" data-testid="sizing-chart-page">
      <Header onOpenLeadForm={onOpenLeadForm} onOpenEstimateForm={onOpenEstimateForm} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-600 py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">What Size AC Unit Do I Need?</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">Proper AC sizing balances comfort, humidity control, and energy efficiency. Goodman AC systems are sized in tons, with standard residential units ranging from 1.5 to 5 tons.</p>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-16">
        <div className="space-y-12">

          {/* Sizing Table */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Goodman AC Sizing Guidelines (Square Footage)</h2>
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full" data-testid="sizing-table">
                <thead>
                  <tr className="bg-emerald-600 text-white">
                    <th className="text-left py-3 px-6 font-semibold">Size (Tons)</th>
                    <th className="text-left py-3 px-6 font-semibold">Square Footage</th>
                  </tr>
                </thead>
                <tbody>
                  {SIZING_DATA.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-3 px-6 font-semibold text-gray-900">{row.tons}</td>
                      <td className="py-3 px-6 text-gray-600">{row.sqft}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-500 mt-3">Generally requiring 1 ton per 500-600 sq ft. You can identify the size by looking at the model number on the outdoor unit.</p>
          </section>

          {/* Key Considerations */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Considerations</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'Climate zone',
                'Ductwork condition',
                'Number and style of windows',
                'Natural shade or sun exposure',
                'Quality and amount of insulation',
                'Number of people using the space',
                'Heat-generating appliances',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <span className="text-emerald-500 font-bold">&#10003;</span>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Climate & Insulation */}
          <section className="grid sm:grid-cols-2 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <ThermometerSun className="w-5 h-5 text-emerald-600" />
                <h3 className="font-bold text-gray-900">Climate & Insulation</h3>
              </div>
              <p className="text-gray-600 text-sm">If your home is not well insulated, has older windows, or you live in a very hot climate, it is wise to select a larger system within your square footage range.</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Home className="w-5 h-5 text-emerald-600" />
                <h3 className="font-bold text-gray-900">Two-Story Homes</h3>
              </div>
              <p className="text-gray-600 text-sm">Two-story homes may require different or zoned systems.</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <h3 className="font-bold text-gray-900">Excess Capacity</h3>
              </div>
              <p className="text-gray-600 text-sm">A unit that is too large will not dehumidify properly, while one that is too small will not cool enough. Once you decide to purchase a unit, our installation partners will come to your home and verify the necessary size and make recommendations.</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Sun className="w-5 h-5 text-orange-500" />
                <h3 className="font-bold text-gray-900">Solar Radiation</h3>
              </div>
              <p className="text-gray-600 text-sm">In Florida, direct sunlight on your home for most of the day can cause tremendous heat build up. The direction your home faces and how much shade it receives can also be a factor in the required unit size.</p>
            </div>
          </section>

          {/* SEER2 Rating */}
          <section className="bg-gray-50 border border-gray-200 rounded-xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <Info className="w-5 h-5 text-emerald-600" />
              <h2 className="text-xl font-bold text-gray-900">What is a SEER2 Rating?</h2>
            </div>
            <p className="text-gray-600">A SEER2 ratio is a calculation used to measure the energy efficiency of a specific HVAC system. A unit's SEER2 rating is reached by dividing its cooling output (over an average cooling season) by the total amount of energy used. The final number reflects a system's maximum efficiency rating under ideal conditions. Systems that use energy more efficiently have a higher SEER2 ratio. Most common systems have a rating between 14 and 16 SEER2, while more expensive, high-efficiency systems can have ratings as high as 22 SEER2.</p>
          </section>

          {/* Tonnage */}
          <section className="bg-gray-50 border border-gray-200 rounded-xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <Info className="w-5 h-5 text-emerald-600" />
              <h2 className="text-xl font-bold text-gray-900">What is a Tonnage/BTUh Rating?</h2>
            </div>
            <p className="text-gray-600">Tonnage or BTUh ratings indicate the amount of energy it takes an air conditioner or HVAC system to remove heat from a home in a 1-hour period. A 1-ton rating is equal to a 12,000 BTUh rating. Ratings typically break at 0.5-ton points creating a 1.5, 2, 2.5, 3, and so on rating scale. Choosing the right system depends on the square footage of your home and the climate where you live.</p>
          </section>

          {/* Model Number Chart */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Read a Goodman Model Number</h2>
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full" data-testid="model-number-table">
                <thead>
                  <tr className="bg-emerald-600 text-white">
                    <th className="text-left py-3 px-6 font-semibold">Model Number Digit</th>
                    <th className="text-left py-3 px-6 font-semibold">Ton Size</th>
                  </tr>
                </thead>
                <tbody>
                  {MODEL_DATA.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-3 px-6 font-mono font-semibold text-gray-900">{row.digit}</td>
                      <td className="py-3 px-6 text-gray-600">{row.tons}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Why Size Matters */}
          <section className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Why HVAC Unit Size Matters</h2>
            <p className="text-gray-600 mb-6">Choosing the right size HVAC unit for your home can have a large impact on year-round comfort, energy efficiency, and the lifespan of your equipment. There is a process to determine the proper unit size for your space - and bigger isn't always better.</p>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h3 className="font-bold text-red-700 mb-3">Too Small</h3>
                <p className="text-gray-600 text-sm">An air conditioner that's too small can lead to overworked equipment that runs constantly, raising your utility bills and rarely reaching or maintaining desired indoor temperatures. Along with higher energy costs, an undersized system can also cause uneven cooling throughout your home.</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h3 className="font-bold text-red-700 mb-3">Too Large</h3>
                <p className="text-gray-600 text-sm">While an oversized system will reach desired indoor temperatures quickly, this causes them to cycle on and off frequently. Repeating this cycle too often wastes energy and causes undue wear and tear on the unit. Plus, with air flow happening for only part of the day, humidity can rise making the home uncomfortable and possibly leading to mold or mildew build-up.</p>
              </div>
            </div>
          </section>

          {/* Choose Professional */}
          <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Choose a Qualified Installation Professional</h2>
            <p className="text-gray-600">Now that you've determined the right air conditioner size for your home and identified the system that's right for you, it's time to think about installation. Our Licensed and Insured AC partners were chosen because of their experience, quality of work and outstanding customer reviews.</p>
          </section>

        </div>
      </main>

      <Footer />
      <AccessibilityWidget />
    </div>
  );
}
